// ─── GBCOSPACK — Quotation Template Studio ───────────────────────────────────
// /admin/quotation-template
// Admin-only design & validation environment.
// Uses the same QuotationHtmlDocument and QuotationPdfDocument production
// components — NOT preview-only copies.

import { useState, useMemo } from 'react';
import type { DocumentLocale, DocumentType } from '@/quotation/document/types';
import type { ScenarioKey } from '@/quotation/fixtures/quotationFixtures';
import { getFixture } from '@/quotation/fixtures/quotationFixtures';
import StudioControls from './StudioControls';
import StudioPreview from './StudioPreview';

type PreviewMode = 'html' | 'pdf' | 'side-by-side';

export default function QuotationTemplatePage() {
  const [docType, setDocType] = useState<DocumentType>('preliminary_quote');
  const [locale, setLocale] = useState<DocumentLocale>('en');
  const [scenario, setScenario] = useState<ScenarioKey>('standard');
  const [mode, setMode] = useState<PreviewMode>('html');

  const data = useMemo(
    () => getFixture(scenario, locale, docType),
    [scenario, locale, docType],
  );

  return (
    <div>
      <div className="px-4 md:px-6 lg:px-8 py-6 space-y-5">
        {/* Notice banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <i className="ri-information-line text-amber-600 text-base mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>Step 1 — Design Mode.</strong>{' '}
            This studio uses mock fixtures only. Real customer quotation APIs are not connected yet.
            Changes to HTML/PDF components here become the production templates.
          </p>
        </div>

        {/* Controls */}
        <StudioControls
          docType={docType}
          locale={locale}
          scenario={scenario}
          mode={mode}
          onDocTypeChange={setDocType}
          onLocaleChange={setLocale}
          onScenarioChange={setScenario}
          onModeChange={setMode}
        />

        {/* Preview */}
        <StudioPreview data={data} mode={mode} />
      </div>
    </div>
  );
}